import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'localization';
import './PublisherIndexOrganism.scss';
import { usePublishers } from '../../../hooks/usePublishers';
import PublisherCardMolecule from '../../molecules/marketplace/PublisherCardMolecule';
import PublisherFeaturedSpotlightOrganism from './PublisherFeaturedSpotlightOrganism';
import type { PublisherDetail } from 'types/publisher';

type FilterOption = 'all' | 'mine' | 'pending';

export interface PublisherIndexOrganismProps {
  onBack?: () => void;
  onCreatePublisher?: () => void;
  className?: string;
}

export const PublisherIndexOrganism: React.FC<PublisherIndexOrganismProps> = ({
  onBack,
  onCreatePublisher,
  className = '',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterOption>('all');

  const {
    publishers: allPublishers,
    loading,
    error,
    refetch,
  } = usePublishers({ status: 'published', limit: 50 });

  const { publishers: myPublishers } = usePublishers({ mine: true, limit: 200 });

  // Marketplace managers (manage_marketplace) get the pending-review approval
  // queue. The backend returns an empty list to everyone else for this request,
  // so the chip/section below is gated purely on data presence — which also
  // covers standalone marketplace_managers, not just platform admins.
  const { publishers: pendingPublishers } = usePublishers({
    status: 'pending_review',
    limit: 100,
  });

  const searchTrimmed = search.trim();

  const visiblePublishers = useMemo(() => {
    const base: PublisherDetail[] =
      filter === 'mine'
        ? myPublishers
        : filter === 'pending'
        ? pendingPublishers
        : allPublishers;
    if (!searchTrimmed) return base;
    const q = searchTrimmed.toLowerCase();
    return base.filter(
      (p) =>
        p.display_name.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q) ||
        (p.bio || '').toLowerCase().includes(q)
    );
  }, [filter, allPublishers, myPublishers, pendingPublishers, searchTrimmed]);

  const featured = useMemo(
    () =>
      allPublishers.find((p) => p.is_featured) ??
      allPublishers.find((p) => p.is_official && p.status === 'published') ??
      null,
    [allPublishers]
  );

  const showFeatured = filter === 'all' && !searchTrimmed && featured !== null;

  const sectionTitle = useMemo(() => {
    if (searchTrimmed) return t('marketplace.publishers.index.sectionSearch');
    if (filter === 'mine') return t('marketplace.publishers.index.sectionMine');
    if (filter === 'pending') return t('marketplace.publishers.index.sectionPending');
    return showFeatured
      ? t('marketplace.publishers.index.sectionAll')
      : t('marketplace.publishers.index.title');
  }, [searchTrimmed, filter, showFeatured, t]);

  const handleClick = useCallback(
    (slug: string) => navigate(`/marketplace/publishers/${encodeURIComponent(slug)}`),
    [navigate]
  );

  const classes = ['marketplace-publisher-index', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {/* Breadcrumb — matches the approach-detail pattern:
          ← Marketplace › Publishers
          The leading arrow comes from CSS `::before` on the nav element;
          clicking "Marketplace" fires onBack (same as the old back button). */}
      <nav
        className="marketplace-publisher-index__breadcrumb"
        aria-label={t('marketplace.publishers.index.breadcrumb.publishers')}
      >
        {onBack ? (
          <button
            type="button"
            className="marketplace-publisher-index__breadcrumb-link"
            onClick={onBack}
          >
            {t('marketplace.publishers.index.breadcrumb.marketplace')}
          </button>
        ) : (
          <span className="marketplace-publisher-index__breadcrumb-link">
            {t('marketplace.publishers.index.breadcrumb.marketplace')}
          </span>
        )}
        <span
          className="marketplace-publisher-index__breadcrumb-sep"
          aria-hidden="true"
        >
          {'›'}
        </span>
        <strong className="marketplace-publisher-index__breadcrumb-current">
          {t('marketplace.publishers.index.breadcrumb.publishers')}
        </strong>
      </nav>

      {/* Title row: h1 + subtitle + actions */}
      <div className="marketplace-publisher-index__title-row">
        <div className="marketplace-publisher-index__title-block">
          <h1 className="marketplace-publisher-index__title">
            {t('marketplace.publishers.index.title')}
          </h1>
          <p className="marketplace-publisher-index__subtitle">
            {t('marketplace.publishers.index.subtitle')}
          </p>
        </div>
        <div className="marketplace-publisher-index__actions">
          {myPublishers.length > 0 && (
            <button
              type="button"
              className="marketplace-publisher-index__btn marketplace-publisher-index__btn--secondary"
              onClick={() => setFilter('mine')}
            >
              <i className="pi pi-bookmark" aria-hidden="true" />
              {t('marketplace.publishers.index.myPublishers')}
              <span className="marketplace-publisher-index__count-badge">{myPublishers.length}</span>
            </button>
          )}
          {onCreatePublisher && (
            <button
              type="button"
              className="marketplace-publisher-index__btn marketplace-publisher-index__btn--primary"
              onClick={onCreatePublisher}
            >
              <i className="pi pi-plus" aria-hidden="true" />
              {t('marketplace.publishers.index.newPublisher')}
            </button>
          )}
        </div>
      </div>

      {/* Search + filter chips */}
      <div className="marketplace-publisher-index__controls">
        <div className="marketplace-publisher-index__search-bar">
          <i className="pi pi-search marketplace-publisher-index__search-icon" aria-hidden="true" />
          <input
            type="search"
            className="marketplace-publisher-index__search"
            placeholder={t('marketplace.publishers.index.searchPlaceholder')}
            aria-label={t('marketplace.publishers.index.searchAriaLabel')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="marketplace-publisher-index__filter-chips" role="group" aria-label="Filter">
          <button
            type="button"
            className={[
              'marketplace-publisher-index__chip',
              filter === 'all' ? 'marketplace-publisher-index__chip--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setFilter('all')}
          >
            {t('marketplace.publishers.index.filterAll')}
          </button>
          <button
            type="button"
            className={[
              'marketplace-publisher-index__chip',
              filter === 'mine' ? 'marketplace-publisher-index__chip--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setFilter('mine')}
          >
            {t('marketplace.publishers.index.filterMine', { count: myPublishers.length })}
          </button>
          {pendingPublishers.length > 0 && (
            <button
              type="button"
              className={[
                'marketplace-publisher-index__chip',
                'marketplace-publisher-index__chip--review',
                filter === 'pending' ? 'marketplace-publisher-index__chip--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setFilter('pending')}
            >
              {t('marketplace.publishers.index.filterPending', {
                count: pendingPublishers.length,
              })}
            </button>
          )}
        </div>
      </div>

      {/* Featured spotlight (conditional) */}
      {showFeatured && featured && (
        <PublisherFeaturedSpotlightOrganism publisher={featured} />
      )}

      {/* Loading / error states */}
      {loading && allPublishers.length === 0 && (
        <div className="marketplace-publisher-index__loading">
          <span className="marketplace-marketplace__spinner" />
          <p>{t('marketplace.common.loading')}</p>
        </div>
      )}

      {error && (
        <div className="marketplace-publisher-index__error">
          <p className="marketplace-marketplace__error-message">{error}</p>
          <button type="button" className="marketplace-marketplace__retry-btn" onClick={refetch}>
            {t('marketplace.common.retry')}
          </button>
        </div>
      )}

      {/* Section heading + grid */}
      {!loading && !error && (
        <>
          <div className="marketplace-publisher-index__section-header">
            <h2 className="marketplace-publisher-index__section-title">{sectionTitle}</h2>
            <span className="marketplace-publisher-index__section-meta">
              {t('marketplace.publishers.index.publisherCount', {
                count: visiblePublishers.length,
              })}
            </span>
          </div>

          {visiblePublishers.length > 0 ? (
            <div className="marketplace-publisher-index__grid">
              {visiblePublishers.map((pub: PublisherDetail) => (
                <PublisherCardMolecule
                  key={pub.slug}
                  publisher={pub}
                  onClick={handleClick}
                />
              ))}
            </div>
          ) : (
            <div className="marketplace-publisher-index__empty">
              <div className="marketplace-publisher-index__empty-icon">
                <i className="pi pi-search-minus" aria-hidden="true" />
              </div>
              <p className="marketplace-publisher-index__empty-title">
                {t('marketplace.publishers.index.noPublishersTitle')}
              </p>
              <p className="marketplace-publisher-index__empty-tip">
                {searchTrimmed
                  ? t('marketplace.publishers.index.noSearchResults')
                  : t('marketplace.publishers.index.empty')}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PublisherIndexOrganism;
